/** @jsxImportSource @emotion/react */

// libraries
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { css } from "@emotion/react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  Tooltip,
  Typography
} from "@mui/material";

// model, data
import {
  HALF_AND_QUARTERS,
  HalfYear,
  MONTHS,
  Quarter,
  YearMonth
} from "data/period.data";
import {
  REVIEW_COL_DATA,
  ReviewBankStatementColumnData,
  ReviewCardBuyColumnData,
  ReviewExtraInvoiceColumnData,
  ReviewTaxInvoiceColumnData
} from "./column.data";
import { PALETTE } from "styles/base/variables";
import {
  Business,
  CardBuy,
  MenuType,
  MultiAccountCodeAmount,
  Store,
  StoreType,
  SUB_TYPE_PARAM_KEYS,
  SubType,
  UpdateTxData
} from "models/business.model";
import { SUBJECT_DATA, SUBJECT_LIST } from "data/subject.data";
import { SAMPLE_CARDBUY_DATA } from "data/sample.data";
import {
  BankStatement,
  BankStatementWithAdditionalData
} from "models/bank.model";
import { ExtraInvoice, TaxInvoice, TaxItem } from "models/tax.model";
import { bankAccountNoSplitPipe } from "pipes/bank.pipe";

// components
import Table from "components/common/Table";
import BaseSelect from "components/common/Select/Base";
import BaseInput from "components/common/Input/Base";
import SubtitleCard from "components/common/Card/Subtitle/SubtitleCard";
import BaseButton from "components/common/Button/Base";
import ReportButton from "components/Report/Button";
import AiSuggestionButton from "components/AiSuggestion/Button";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import MultiAccountDialog from "./Dialog/MultiAccount";
import SelectStoreDialog, {
  SelectStoreDialogSettings
} from "./Dialog/SelectStore";
import InvoiceSumCheckDialog from "./Dialog/InvoiceSumCheck";

// utils, api
import {
  AiSuggestionResult,
  BankAiSuggestionResult,
  BaseParamData,
  deleteExtraInvoices,
  downloadNewzenExcel,
  getBankStatementAiSuggestion,
  getCardBuyAiSuggestion,
  getExtraInvoiceStore,
  getStore,
  getTaxInvoiceBuyAiSuggestion,
  getTaxInvoiceSalesAiSuggestion,
  getUserInfo,
  TaxInvoiceBuyAiSuggestionResult,
  TaxInvoiceSalesAiSuggestionResult,
  updateConfirmation,
  updateExtraInvoiceConfirmation,
  updateExtraInvoiceTransaction,
  updateTransaction
} from "pages/remotes";
import {
  convertCurrency,
  convertDateToPeriod,
  convertQuarterAndHalfToDate,
  convertSearchDatesToInvoiceSumCheckDate,
  convertYearMonthToDate,
  reverseArray,
  sortList
} from "utils/converter";
import { renderDownload } from "utils/renderer";
import { isTypingContext } from "utils/dom";

// icons
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { SubjectCodeData } from "models/subject.model";
import { UserInfo } from "models/user.model";

// AI 회계처리 버튼 제한 노출 대상 userId
const AI_SUGGESTION_ALLOWED_USER_IDS = [
  "686b8ae18d3a9f106c183b9b",
  "6613891e1e25bd7766c3a5b8",
  "63228079169db80cbefcbe07",
  "69b763b868108e59ba10b5b1",
  "6a5afff8cfe5f27f5680c5f3",
  "6a64480c4f06207de7590aff",
  "6a795d14a7a7eb647abd5250",
  "630f7002c79a104cb950ed9b"
];

// AI 회계처리 추천값이 원본과 같아지면(재추천으로 되돌아간 경우) 이전에 남아있던
// 변경 필드를 지워야 하이라이트/표시값이 원본으로 정상 복귀한다.
const mergeAiSuggestionIntoModifiedMap = (
  tmpMCB: Map<string, UpdateTxData>,
  id: string,
  suggestedAccountCode: string | undefined,
  originalAccountCode: string | undefined | null,
  suggestedIsVatDeduction: boolean | null | undefined,
  originalIsVatDeduction: boolean | undefined | null
) => {
  const isAccountCodeChanged =
    suggestedAccountCode !== undefined &&
    suggestedAccountCode !== originalAccountCode;
  const isDdcChanged =
    suggestedIsVatDeduction !== undefined &&
    suggestedIsVatDeduction !== null &&
    suggestedIsVatDeduction !== originalIsVatDeduction;

  const existing = tmpMCB.get(id);
  const updated: UpdateTxData = existing ? { ...existing } : { id };

  if (suggestedAccountCode !== undefined) {
    if (isAccountCodeChanged) updated.accountCode = suggestedAccountCode;
    else delete updated.accountCode;
  }
  if (
    suggestedIsVatDeduction !== undefined &&
    suggestedIsVatDeduction !== null
  ) {
    if (isDdcChanged) updated.isVatDeduction = suggestedIsVatDeduction;
    else delete updated.isVatDeduction;
  }

  const hasRemainingChange =
    updated.accountCode !== undefined ||
    updated.isVatDeduction !== undefined ||
    (updated.multiAccountCodes?.length || 0) > 0;

  if (hasRemainingChange) tmpMCB.set(id, updated);
  else tmpMCB.delete(id);
};

const buildAiReasoningMap = (
  items: { id: string; reasoning?: string }[]
): Map<string, string> => {
  const map = new Map<string, string>();
  items.forEach((item) => {
    if (item.reasoning) map.set(item.id, item.reasoning);
  });
  return map;
};

// AI 회계처리 1회 API 호출당 최대 처리 건수
const AI_SUGGESTION_CHUNK_SIZE = 100;

const chunkArray = <T,>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

function Review(data: { storeType: StoreType; subType?: SubType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const storeType: StoreType = data.storeType;
  const subType: SubType | undefined = data.subType;
  const menuType = (
    subType ? `${storeType}-${subType.toLowerCase()}` : storeType
  ) as MenuType;

  const locationBizId = location.pathname.split("/")?.[3];
  const [searchParams, setSearchParams] = useSearchParams();
  const [spStartDate, spEndDate, spSort, spSample] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
    searchParams.get("sort"),
    searchParams.get("sample")
  ];

  const sumCheckDate = useMemo(
    () => convertSearchDatesToInvoiceSumCheckDate(spStartDate, spEndDate),
    [spStartDate, spEndDate]
  );
  const showInvoiceSumCheck =
    menuType === "tax-buy" || menuType === "tax-sales";
  const isExtraMenu = menuType === "extra-buy" || menuType === "extra-sales";
  const inputKeywordRef = useRef<HTMLInputElement>(null);
  const inputSubjectRef = useRef<HTMLInputElement>(null);
  const optionSubjectRef = useRef<HTMLUListElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isAiSuggestionLoading, setIsAiSuggestionLoading] = useState(false);
  const [isAiSuggestionProgressOpen, setIsAiSuggestionProgressOpen] =
    useState<boolean>(false);
  const [aiSuggestionProgress, setAiSuggestionProgress] = useState<{
    total: number;
    completed: number;
  }>({ total: 0, completed: 0 });
  const [
    isAiSuggestionCancelConfirmOpen,
    setIsAiSuggestionCancelConfirmOpen
  ] = useState<boolean>(false);
  const aiSuggestionCancelRef = useRef<boolean>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [business, setBusiness] = useState<Business | null>(null); // BusinessWithCardBuyList | BusinessWithBankStatementList | BusinessWithTaxInvoiceList 이지만 편의상 Business로 설정
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const isAiSuggestionAllowed = Boolean(
    userInfo?.id && AI_SUGGESTION_ALLOWED_USER_IDS.includes(userInfo.id)
  );

  useEffect(() => {
    getUserInfo()
      .then((res) => setUserInfo(res.data))
      .catch((err) => console.error(err));
  }, []);

  const [columnList, setColumnList] = useState<
    (
      | ReviewCardBuyColumnData
      | ReviewBankStatementColumnData
      | ReviewTaxInvoiceColumnData
      | ReviewExtraInvoiceColumnData
    )[]
  >([]);

  const [dataList, setDataList] = useState<
    (CardBuy | BankStatementWithAdditionalData)[]
  >([]);
  const [sortedDataList, setSortedDataList] = useState<
    (CardBuy | BankStatementWithAdditionalData)[]
  >([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredDataList, setFilteredDataList] = useState<
    (CardBuy | BankStatementWithAdditionalData)[]
  >([]);
  const [checkedDataSet, setCheckedDataSet] = useState<Set<string>>(new Set());
  const [modifiedDataMap, setModifiedDataMap] = useState<
    Map<string, UpdateTxData>
  >(new Map());
  // AI 회계처리가 마지막으로 추천한 계정코드. 되돌리기를 눌러 원본으로
  // 복귀시킨 뒤에도 남겨둬서, 다시 누르면 AI 추천값으로 재적용할 수 있게 한다.
  const [aiAccountCodeMap, setAiAccountCodeMap] = useState<
    Map<string, string>
  >(new Map());

  // Ctrl/Cmd+Z 되돌리기 히스토리. 액션(토글/일괄변경/붙여넣기/AI 회계처리 등)
  // 하나당 스냅샷 하나 — 여러 행이 한 번에 바뀌어도 되돌리기 한 번으로 전부 복귀한다.
  // 렌더를 유발할 필요 없는 순수 히스토리라 ref로 관리한다.
  const undoStackRef = useRef<
    { modifiedDataMap: Map<string, UpdateTxData>; aiAccountCodeMap: Map<string, string> }[]
  >([]);
  const redoStackRef = useRef<
    { modifiedDataMap: Map<string, UpdateTxData>; aiAccountCodeMap: Map<string, string> }[]
  >([]);

  // modifyDdc/modifySubjectCode/restoreAccountCode는 UpdateTxData 객체를
  // 제자리에서(in-place) 수정한다. 얕은 Map 복사만 하면 스냅샷과 실제 상태가
  // 같은 객체를 공유하게 되어, 이후 수정이 이미 찍어둔 스냅샷까지 오염시킨다.
  // 엔트리별로 새 객체를 만들어 완전히 독립된 스냅샷을 보장한다.
  const cloneModifiedDataMap = (
    map: Map<string, UpdateTxData>
  ): Map<string, UpdateTxData> =>
    new Map(
      Array.from(map, ([id, entry]) => [
        id,
        {
          ...entry,
          multiAccountCodes: entry.multiAccountCodes
            ? [...entry.multiAccountCodes]
            : entry.multiAccountCodes
        }
      ])
    );

  const pushUndoSnapshot = () => {
    undoStackRef.current.push({
      modifiedDataMap: cloneModifiedDataMap(modifiedDataMap),
      aiAccountCodeMap: new Map(aiAccountCodeMap)
    });
    redoStackRef.current = [];
  };

  const serializeModifiedDataMap = (map: Map<string, UpdateTxData>) =>
    JSON.stringify(
      Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
    );

  // 실제로 값이 바뀔 때만 되돌리기 스냅샷을 남긴다. 예를 들어 이미 830인
  // 계정코드에 830을 붙여넣는 것처럼 결과적으로 아무것도 안 바뀌는 수정은
  // 되돌리기 단계로 잡히지 않아야 한다.
  const runWithUndo = (mutate: () => void) => {
    const snapshot = {
      modifiedDataMap: cloneModifiedDataMap(modifiedDataMap),
      aiAccountCodeMap: new Map(aiAccountCodeMap)
    };
    const beforeSerialized = serializeModifiedDataMap(modifiedDataMap);

    mutate();

    if (serializeModifiedDataMap(modifiedDataMap) === beforeSerialized) return;

    undoStackRef.current.push(snapshot);
    redoStackRef.current = [];
  };

  const undoModification = () => {
    const prev = undoStackRef.current.pop();
    if (!prev) return;
    redoStackRef.current.push({
      modifiedDataMap: cloneModifiedDataMap(modifiedDataMap),
      aiAccountCodeMap: new Map(aiAccountCodeMap)
    });
    setModifiedDataMap(prev.modifiedDataMap);
    setAiAccountCodeMap(prev.aiAccountCodeMap);
  };

  const redoModification = () => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push({
      modifiedDataMap: cloneModifiedDataMap(modifiedDataMap),
      aiAccountCodeMap: new Map(aiAccountCodeMap)
    });
    setModifiedDataMap(next.modifiedDataMap);
    setAiAccountCodeMap(next.aiAccountCodeMap);
  };

  // Ctrl/Cmd+Z 되돌리기, Ctrl/Cmd+Shift+Z 다시 실행. 검색창/편집중인 셀 등
  // 텍스트 입력 중에는 브라우저 기본 되돌리기 동작을 그대로 둔다.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const modifierPressed = e.ctrlKey || e.metaKey;
      if (!modifierPressed || e.key.toLowerCase() !== "z") return;

      if (isTypingContext()) return;

      e.preventDefault();
      if (e.shiftKey) redoModification();
      else undoModification();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modifiedDataMap, aiAccountCodeMap]);

  const [isSubjectListOpen, setIsSubjectListOpen] = useState(false);
  const [modifiedSubjectList, setModifiedSubjectList] = useState<
    { value: string; label: string }[]
  >([]);
  const [filteredSubjectList, setFilteredSubjectList] =
    useState<{ value: string; label: string }[]>(modifiedSubjectList);
  const [subjectCodeDataList, setSubjectCodeDataList] = useState<
    SubjectCodeData[]
  >([]);

  const [selectedDdcAll, setSelectedDdcAll] = useState<string>("");
  const [isUnsavedAlertOpen, setIsUnsavedAlertOpen] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: ""
  });
  const [isShowUnprocessed, setIsShowUnprocessed] = useState<boolean>(false);
  const [isShowSelectedOnly, setIsShowSelectedOnly] =
    useState<boolean>(false);
  const [isShowNotSent, setIsShowNotSent] = useState<boolean>(false);
  const [isMultiAccountDialogOpen, setIsMultiAccountDialogOpen] =
    useState<boolean>(false);
  const [selectedMAData, setSelectedMAData] =
    useState<BankStatementWithAdditionalData | null>(null);

  const [storeList, setStoreList] = useState<Store[]>([]);
  const [isSaveOptionOpen, setIsSaveOptionOpen] = useState<boolean>(false);
  const [isRegisterAccountCode, setIsRegisterAccountCode] =
    useState<boolean>(false);
  const [isSelectDialogOpen, setIsSelectStoreDialogOpen] =
    useState<boolean>(false);
  const [selectedStoreDialogSettings, setSelectedStoreDialogSettings] =
    useState<SelectStoreDialogSettings>({
      storeId: null,
      dataIds: [],
      isCancel: false
    });
  const [isInvoiceSumCheckOpen, setIsInvoiceSumCheckOpen] =
    useState<boolean>(false);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] =
    useState<boolean>(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  useEffect(() => {
    const tmpColumns = REVIEW_COL_DATA[menuType] || [];
    const ref = reviewHandlersRef;
    if (tmpColumns.length === 0) {
      setAlertDialogData((prev) => ({
        ...prev,
        type: "error",
        message: "테이블 칼럼을 지원하지 않는 스토어 타입입니다."
      }));
      setIsAlertOpen(true);
      return;
    }
    setColumnList(
      tmpColumns.map((col) => {
        switch (col.id) {
          case "isVatDeduction":
            return {
              ...col,
              toggle: {
                submit: (value, dataId) => {
                  const changedDdc = !value;
                  ref.current.modifyDdc([
                    { isVatDeduction: changedDdc, id: dataId }
                  ]);
                }
              },
              copyable: true,
              copyValue: (data: any) => String(Boolean(data.isVatDeduction))
            };
          case "storeName":
            if (storeType === "bank") {
              return {
                ...col,
                openDialog: (d: BankStatementWithAdditionalData) =>
                  ref.current.openSelectStoreDialog(d),
                cancel: (data: BankStatementWithAdditionalData) =>
                  ref.current.openSelectStoreDialog(data, { isClose: true })
              };
            } else return col;
          case "accountCode":
            const extendedCol:
              | ReviewCardBuyColumnData
              | ReviewBankStatementColumnData
              | ReviewTaxInvoiceColumnData
              | ReviewExtraInvoiceColumnData = {
              ...col,
              input: {
                submit: (value, dataId) => {
                  ref.current.runWithUndo(() =>
                    ref.current.modifySubjectCode(value, dataId)
                  );
                },
                options: modifiedSubjectList
              },
              restore: ({
                dataId,
                colId
              }: {
                dataId: string;
                colId: string;
              }) => {
                if (colId === "accountCode")
                  ref.current.restoreAccountCode(dataId);
              },
              restoreAvailable: (data: any) =>
                ref.current.isAiRestoreAvailable(data.id),
              copyable: true,
              // 다중 계정코드 행은 accountCode가 "100 +2" 같은 표시용
              // 문자열로 덮어써져 있어 복사 대상에서 제외한다.
              copyValue: (data: any) =>
                data.multiAccountCodes?.length ? "" : data.accountCode || ""
            };

            if (storeType === "bank")
              return {
                ...extendedCol,
                openDialog: (d: BankStatementWithAdditionalData) =>
                  ref.current.openMultiAccountCodeDialog(d)
              };
            else return extendedCol;
          default:
            return col;
        }
      })
    );
  }, [menuType, modifiedSubjectList, storeType]);

  useEffect(() => {
    setSortedDataList(sortList([...dataList], spSort));
  }, [spSort]);

  useEffect(() => {
    const keyword = inputKeywordRef.current?.value || "";
    if (keyword)
      inputKeywordHandler({
        currentTarget: { value: keyword }
      } as React.FormEvent<HTMLInputElement>); // 검색어가 있는 경우 검색어 필터링
  }, [sortedDataList]);

  useEffect(() => {
    loadStore();
  }, [spStartDate, spEndDate]);

  useEffect(() => {
    if (checkedDataSet.size === 0) {
      setSelectedDdcAll("");
      if (isSubjectListOpen) setIsSubjectListOpen(false);
    }
  }, [checkedDataSet]);

  useEffect(() => {
    if (!isSubjectListOpen) resetInputOption();
  }, [isSubjectListOpen]);

  const loadStore = () => {
    if (!(spStartDate && spEndDate)) {
      const tmpAlertData = { ...alertDialogData };
      tmpAlertData.type = "error";
      tmpAlertData.message = "검색 기간이 없습니다. 검색 기간을 설정해주세요.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      return;
    }

    setIsLoading(true);
    let paramData: BaseParamData = {
      startDate: spStartDate!,
      endDate: spEndDate!
    };
    const subTypeKey = SUB_TYPE_PARAM_KEYS[storeType] as SubType;
    if (subType && subTypeKey) {
      paramData = {
        ...paramData,
        [subTypeKey]: subType
      };
    }

    const storeRequest =
      storeType === "extra" && subType
        ? getExtraInvoiceStore(subType, locationBizId, {
            startDate: spStartDate!,
            endDate: spEndDate!
          })
        : getStore(storeType, locationBizId, paramData);

    storeRequest
      .then((res) => {
        const resData = res.data;
        if (resData) {
          setBusiness(resData);
          let dataList: (
            | CardBuy
            | BankStatement
            | TaxInvoice
            | ExtraInvoice
          )[] = [];
          let stores: Store[] = [];
          let sbjData: { [key: string]: string } = {};
          let sbjList: SubjectCodeData[] = resData.accountCodes || [];
          let sbjModiList: { value: string; label: string }[] = [];

          if (storeType === "card") {
            if (spSample) dataList = SAMPLE_CARDBUY_DATA;
            else dataList = resData.cardBuyList;
          } else if (storeType === "bank") {
            dataList = resData.bankStatementList;
            stores = resData.stores || [];
          } else if (storeType === "tax") dataList = resData.invoiceList;
          else if (storeType === "extra") dataList = resData.invoiceList;

          if (sbjList.length > 0) {
            sbjData = sbjList.reduce(
              (acc, curr) => {
                acc[curr.code] = curr.name;
                return acc;
              },
              {} as { [key: string]: string }
            );

            sbjModiList = sbjList.map((subject) => ({
              value: subject.code,
              label: `${subject.name} (${subject.code})`
            }));
          } else {
            sbjData = SUBJECT_DATA;
            sbjModiList = SUBJECT_LIST.map((subject) => ({
              ...subject,
              label: subject.label + ` (${subject.value})`
            }));
          }

          setSubjectCodeDataList(sbjList);
          setModifiedSubjectList(sbjModiList);
          setFilteredSubjectList(sbjModiList);
          setStoreList(stores);

          if (dataList && dataList.length > 0) {
            const dataListWithConvertedInfo = dataList.map((data) => {
              const subjectCode = data.accountCode || "";
              const subjectName = sbjData[subjectCode] || "";

              const additionalData: any = {};
              additionalData.accountName = subjectName;
              if (storeType === "bank") {
                const tmpData = data as BankStatement;
                const isInAmt =
                  tmpData.inAmt > 0 ||
                  tmpData.inAmt < 0 ||
                  tmpData.inAmt - tmpData.outAmt === 0;

                additionalData.dealType =
                  tmpData.inAmt !== null && isInAmt ? "입금" : "출금";

                additionalData.amount = isInAmt
                  ? tmpData.inAmt
                  : tmpData.outAmt;

                additionalData.accNoSplit = bankAccountNoSplitPipe(
                  tmpData.accNo
                );

                if (
                  tmpData.multiAccountCodes &&
                  tmpData.multiAccountCodes.length > 0
                ) {
                  let [accCodeStr, accNameStr] = ["", ""];
                  tmpData.multiAccountCodes.forEach(
                    (acc: MultiAccountCodeAmount) => {
                      if (!accCodeStr) accCodeStr = acc.accountCode;
                      if (accNameStr) accNameStr += ", ";
                      const accCodeName = sbjData[acc.accountCode];
                      if (accCodeName) accNameStr += accCodeName;
                      else accNameStr += acc.accountCode;
                    }
                  );

                  additionalData.accountCode =
                    accCodeStr + " +" + (tmpData.multiAccountCodes.length - 1);
                  additionalData.accountName = accNameStr;
                }

                let isNewzenSent = false;
                if (tmpData.newzenSlipKeyNoList?.length) isNewzenSent = true;
                additionalData.newzenSent = isNewzenSent;
              } else if (storeType === "card") {
                const tmpData = data as CardBuy;

                let isNewzenSent = false;
                if (tmpData.newzenSlipKeyNo) isNewzenSent = true;
                additionalData.newzenSent = isNewzenSent;
              } else if (storeType === "tax") {
                const tmpData = data as TaxInvoice;
                additionalData.tranType =
                  tmpData.supByr === "01"
                    ? "매출"
                    : tmpData.supByr === "02"
                      ? "매입"
                      : "";
                additionalData.taxType = tmpData.type
                  ? tmpData.isZeroTax
                    ? "영세"
                    : tmpData.type.includes("FREE")
                      ? "면세"
                      : "과세"
                  : "";

                const items: TaxItem[] = tmpData.items || [];
                let assortedItemNames = "";
                items.map((item) => {
                  if (item.itemNm) {
                    if (assortedItemNames) assortedItemNames += ", ";
                    assortedItemNames += item.itemNm;
                  }
                });
                additionalData.itemsName = assortedItemNames || "-";

                let isNewzenSent = false;
                if (tmpData.newzenSlipKeyNo) isNewzenSent = true;
                additionalData.newzenSent = isNewzenSent;
              } else if (storeType === "extra") {
                const tmpData = data as ExtraInvoice;

                let isNewzenSent = false;
                if (tmpData.newzenSlipKeyNo) isNewzenSent = true;
                additionalData.newzenSent = isNewzenSent;
              }

              return {
                ...data,
                ...additionalData
              };
            });

            setDataList(dataListWithConvertedInfo);
            setSortedDataList(sortList(dataListWithConvertedInfo, spSort));
          } else {
            setDataList([]);
            setSortedDataList([]);
          }

          setModifiedDataMap(new Map());
          setAiAccountCodeMap(new Map());
          setCheckedDataSet(new Set());
          undoStackRef.current = [];
          redoStackRef.current = [];
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message = "업체 정보를 불러오지 못했습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  const onSelectQuarter = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertQuarterAndHalfToDate(
      value as Quarter | HalfYear
    );
    if (startDate && endDate) {
      searchParams.set("startDate", startDate);
      searchParams.set("endDate", endDate);
    }
    setSearchParams(searchParams);
    setCheckedDataSet(new Set());
  };
  const onSelectMonth = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertYearMonthToDate(value as YearMonth);
    if (startDate && endDate) {
      searchParams.set("startDate", startDate);
      searchParams.set("endDate", endDate);
    }
    setSearchParams(searchParams);
    setCheckedDataSet(new Set());
  };

  const inputKeywordHandler = (e: React.FormEvent<HTMLInputElement>) => {
    const tmpColumns = REVIEW_COL_DATA[menuType] || [];

    const value = e.currentTarget.value || "";
    const loweredVal = value.toLowerCase();
    if (value) {
      setIsFiltered(true);
      let tmpSortedDataList = sortedDataList.filter((cardBuy) => {
        for (const column of tmpColumns) {
          const colId = column.id as keyof (
            | CardBuy
            | BankStatementWithAdditionalData
          );
          const loweredContent = cardBuy[colId]?.toString().toLowerCase() || "";
          if (loweredContent?.includes(loweredVal)) return true;
        }
      });
      setFilteredDataList(tmpSortedDataList);
    } else {
      setIsFiltered(false);
    }
  };

  const hasLockedSelectedData = () =>
    Boolean(business?.isAccountingConfirmed) ||
    Array.from(checkedDataSet).some((id) => {
      const row = dataList.find((item) => item.id === id) as any;
      return Boolean(row?.isAccountingConfirmed);
    });

  const alertLockedSelection = () => {
    const tmpAlertData = { ...alertDialogData };
    tmpAlertData.type = "warning";
    tmpAlertData.message =
      "선택한 데이터 중 마감처리된 데이터가 있어 일괄 변경할 수 없습니다.\n마감된 데이터를 선택에서 제외해주세요.";
    setAlertDialogData(tmpAlertData);
    setIsAlertOpen(true);
  };

  const onSelectDdcAll = (e: React.MouseEvent<HTMLOptionElement>) => {
    if (hasLockedSelectedData()) {
      alertLockedSelection();
      setSelectedDdcAll("");
      return;
    }

    const value = e.currentTarget.value || "";
    const updates: UpdateTxData[] = [];
    checkedDataSet.forEach((id) =>
      updates.push({ isVatDeduction: value === "y", id })
    );
    modifyDdc(updates);
    setSelectedDdcAll("");
  };

  const onSelectSbjAll = (e: React.MouseEvent<HTMLOptionElement>) => {
    if (hasLockedSelectedData()) {
      alertLockedSelection();
      return;
    }

    const value = e.currentTarget.value || "";
    const updates: UpdateTxData[] = [];

    checkedDataSet.forEach((id) => {
      const row = dataList.find((item) => item.id === id) as any;
      // 다중 계정코드로 분할된 행은 단일 코드로 덮어쓰면 분할 내역이 사라지므로 제외한다.
      if (row?.multiAccountCodes?.length) return;
      updates.push({ id, accountCode: value });
    });

    runWithUndo(() => {
      updates.forEach(
        (update) =>
          update.accountCode && modifySubjectCode(update.accountCode, update.id)
      );
    });
  };

  const onSearchSbjAll = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value || "";
    if (value) {
      setFilteredSubjectList(
        modifiedSubjectList.filter((subject) => subject.label.includes(value))
      );
    } else {
      setFilteredSubjectList(modifiedSubjectList);
    }
  };

  const keyDownHanlder = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const event = e as React.KeyboardEvent<HTMLInputElement>;
    const eventKey = event.key;
    const eventTarget = event.target as HTMLInputElement;
    const tgtValue = eventTarget?.value;

    if (eventKey === "Enter") {
      if (tgtValue) {
        const firstMatch = modifiedSubjectList.find((subject) =>
          subject.label.includes(tgtValue)
        );

        if (firstMatch) {
          onSelectSbjAll({
            currentTarget: { value: firstMatch.value }
          } as unknown as React.MouseEvent<HTMLOptionElement>);
        }
      }
    } else if (eventKey === "Escape") {
      setIsSubjectListOpen(false);
    }
  };

  const resetInputOption = () => {
    if (inputSubjectRef.current) inputSubjectRef.current.value = "";
    if (optionSubjectRef.current) optionSubjectRef.current.scrollTop = 0;
    setFilteredSubjectList(modifiedSubjectList);
  };

  const modifyDdc = (updates: UpdateTxData[]) => {
    runWithUndo(() => {
      let tmpMCB;
      updates.forEach((update) => {
        tmpMCB = renderMCBDdc(update, update.id);
      });
      if (tmpMCB) setModifiedDataMap(tmpMCB);
    });
  };

  const renderMCBDdc = (
    update: UpdateTxData,
    dataId: string
  ): Map<string, UpdateTxData> => {
    const tmpMatchedData = dataList.find(
      (data) => data.id === dataId
    ) as CardBuy; // 은행입출금내역은 공제 항목이 없음
    const originalDdc = tmpMatchedData?.isVatDeduction;
    const tmpMCB = modifiedDataMap;
    const isNotChanged = originalDdc === update.isVatDeduction;

    if (tmpMCB.has(dataId)) {
      const newData = tmpMCB.get(dataId);
      if (isNotChanged) {
        if (newData?.accountCode)
          delete newData.isVatDeduction; // 계정과목 변동이 있을 경우 공제 여부 변경만 삭제
        else tmpMCB.delete(dataId); // 계정과목 변동이 없을 경우 삭제
      } else {
        const updatedData: UpdateTxData = {
          id: dataId,
          isVatDeduction: update.isVatDeduction
        };
        if (newData?.accountCode)
          updatedData["accountCode"] = newData.accountCode;
        tmpMCB.set(dataId, updatedData);
      }
    } else {
      if (isNotChanged) {
        console.log("No change");
      } else {
        tmpMCB.set(dataId, {
          id: dataId,
          isVatDeduction: update.isVatDeduction
        });
      }
    }

    return new Map(tmpMCB);
  };

  const modifySubjectCode = (value: string, dataId: string) => {
    if (modifiedDataMap.has(dataId)) {
      const tmpModi = modifiedDataMap.get(dataId);
      if (tmpModi) {
        // 단일 계정과목을 직접 지정하면 AI가 넣어둔 다중계정 분할은 해제한다.
        delete tmpModi.multiAccountCodes;
        const matchedData = dataList.find((data) => data.id === dataId);
        if (matchedData?.accountCode === value) {
          // 계정과목이 변경되지 않았을 경우
          if (tmpModi.isVatDeduction !== undefined)
            delete tmpModi.accountCode; // 공제 여부 변경이 있을 경우 계정과목 변경만 삭제
          else modifiedDataMap.delete(dataId); // 공제 여부 변경이 없을 경우 삭제
        } else {
          tmpModi.accountCode = value;
        }
      }
    } else {
      const matchedData = dataList.find((data) => data.id === dataId);
      // 원본 값과 같으면 변경분으로 남기지 않는다(붙여넣기로 동일한 값이
      // 들어오는 경우가 흔해서, 이 체크가 없으면 실제로는 안 바뀌었는데도
      // 노란색 변경 표시가 뜬다).
      if (matchedData?.accountCode !== value) {
        modifiedDataMap.set(dataId, {
          id: dataId,
          accountCode: value
        });
      }
    }

    setModifiedDataMap(new Map(modifiedDataMap));
  };

  const restoreAccountCode = (dataId: string) => {
    const modifiedCurrData = modifiedDataMap.get(dataId);
    const isCurrentlyChanged = modifiedCurrData?.accountCode !== undefined;

    if (isCurrentlyChanged) {
      // 되돌리기: 수정 중인 계정코드를 지워 원본 값으로 복귀
      pushUndoSnapshot();
      const nextEntry: UpdateTxData = { ...modifiedCurrData };
      delete nextEntry.accountCode;
      // AI가 추천한 다중계정 분할도 대표 계정코드와 함께 되돌린다.
      delete nextEntry.multiAccountCodes;
      const next = new Map(modifiedDataMap);
      const hasRemainingChange = nextEntry.isVatDeduction !== undefined;
      if (hasRemainingChange) next.set(dataId, nextEntry);
      else next.delete(dataId);
      setModifiedDataMap(next);
      return;
    }

    // 다시 되돌리기(redo): AI가 마지막으로 추천했던 계정코드로 재적용
    const aiValue = aiAccountCodeMap.get(dataId);
    if (aiValue === undefined) return;

    pushUndoSnapshot();
    const next = new Map(modifiedDataMap);
    const existing = next.get(dataId);
    next.set(dataId, { ...(existing || { id: dataId }), accountCode: aiValue });
    setModifiedDataMap(next);
  };

  const isAiRestoreAvailable = (dataId: string) => aiAccountCodeMap.has(dataId);

  // 선택된 셀(단일 행) 또는 체크된 행들에 붙여넣기. 붙여넣는 값이
  // true/false면 공제여부, 이 업체의 계정코드 목록에 있는 값이면 계정코드를
  // 바꾼다 — 어느 쪽에도 해당하지 않으면 아무 것도 하지 않는다.
  const tablePaste = (value: string, targetIds: string[]) => {
    if (targetIds.length === 0) return;

    const trimmed = value.trim();
    const isBoolean = trimmed === "true" || trimmed === "false";
    const isKnownAccountCode = subjectCodeDataList.some(
      (subject) => subject.code === trimmed
    );

    if (!isBoolean && !isKnownAccountCode) return;

    // 선택된 여러 건 중 마감된 건만 제외하고, 마감되지 않은 나머지에는 그대로 적용한다.
    // 계정코드를 붙여넣는 경우, 다중 계정코드로 분할된 행은 대상에서 뺀다 —
    // 단일 코드로 덮어쓰면 분할 내역이 사라진다.
    const applicableIds = Boolean(business?.isAccountingConfirmed)
      ? []
      : targetIds.filter((id) => {
          const row = dataList.find((item) => item.id === id) as any;
          if (row?.isAccountingConfirmed) return false;
          if (!isBoolean && row?.multiAccountCodes?.length) return false;
          return true;
        });
    if (applicableIds.length === 0) return;

    if (isBoolean) {
      const isVatDeduction = trimmed === "true";
      modifyDdc(applicableIds.map((id) => ({ id, isVatDeduction })));
    } else {
      runWithUndo(() => {
        applicableIds.forEach((id) => modifySubjectCode(trimmed, id));
      });
    }
  };

  const openSelectStoreDialog = (
    data: BankStatementWithAdditionalData,
    option?: { isClose?: boolean }
  ) => {
    setSelectedStoreDialogSettings({
      storeId: data.storeId,
      dataIds: data.id ? [data.id] : [],
      isCancel: option?.isClose || false
    });
    setIsSelectStoreDialogOpen(true);
  };

  const closeSelectStoreDialog = () => {
    setIsSelectStoreDialogOpen(false);
    setTimeout(() => {
      resetInputOption();
      setSelectedStoreDialogSettings({
        storeId: null,
        dataIds: [],
        isCancel: false
      });
    }, 300);
  };

  const onSelectStoreAll = () => {
    if (hasLockedSelectedData()) {
      alertLockedSelection();
      return;
    }

    const checkedIds = Array.from(checkedDataSet).map((dataId) => dataId);
    setSelectedStoreDialogSettings({
      storeId: null,
      dataIds: checkedIds,
      isCancel: false
    });
    setIsSelectStoreDialogOpen(true);
  };

  const openMultiAccountCodeDialog = (
    data: BankStatementWithAdditionalData
  ) => {
    setSelectedMAData(data);
    setIsMultiAccountDialogOpen(true);
  };

  const closeMultiAccountCodeDialog = () => {
    setSelectedMAData(null);
    setIsMultiAccountDialogOpen(false);
  };

  const reviewHandlersRef = useRef<{
    modifyDdc: (updates: UpdateTxData[]) => void;
    modifySubjectCode: (value: string, dataId: string) => void;
    restoreAccountCode: (dataId: string) => void;
    isAiRestoreAvailable: (dataId: string) => boolean;
    runWithUndo: (mutate: () => void) => void;
    openSelectStoreDialog: (
      data: BankStatementWithAdditionalData,
      option?: { isClose?: boolean }
    ) => void;
    openMultiAccountCodeDialog: (data: BankStatementWithAdditionalData) => void;
  }>({
    modifyDdc: () => {},
    modifySubjectCode: () => {},
    restoreAccountCode: () => {},
    isAiRestoreAvailable: () => false,
    runWithUndo: () => {},
    openSelectStoreDialog: () => {},
    openMultiAccountCodeDialog: () => {}
  });

  reviewHandlersRef.current = {
    modifyDdc,
    modifySubjectCode,
    restoreAccountCode,
    isAiRestoreAvailable,
    runWithUndo,
    openSelectStoreDialog,
    openMultiAccountCodeDialog
  };

  const downloadExcel = (type: StoreType) => {
    const paramData: {
      startDate: string;
      endDate: string;
      invoiceType?: string;
    } = {
      startDate: spStartDate || "2023-01-01",
      endDate: spEndDate || "2023-12-31"
    };

    if (type === "tax") paramData["invoiceType"] = subType || "ALL";

    const tmpAlertData = { ...alertDialogData };
    setIsDownloadLoading(true);
    downloadNewzenExcel(type, locationBizId, paramData).then(
      (res) => {
        setIsDownloadLoading(false);
        renderDownload(res);
        tmpAlertData.type = "success";
        tmpAlertData.message = "엑셀 파일이 다운로드되었습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      },
      (err) => {
        console.error(err);
        setIsDownloadLoading(false);
        tmpAlertData.type = "error";
        tmpAlertData.message = "엑셀 파일 다운로드에 실패했습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      }
    );
  };

  const applyAiSuggestion = () => {
    if (!isAiSuggestionAllowed) return;
    if (!locationBizId || checkedDataSet.size === 0) return;
    if (storeType !== "card" && storeType !== "bank" && storeType !== "tax")
      return;

    const hasConfirmedData = Array.from(checkedDataSet).some((id) => {
      const row = dataList.find((item) => item.id === id) as any;
      return Boolean(row?.isAccountingConfirmed);
    });

    if (hasConfirmedData) {
      const tmpAlertData = { ...alertDialogData };
      tmpAlertData.type = "warning";
      tmpAlertData.message =
        "선택한 데이터 중 마감처리된 데이터가 있어 AI 회계처리를 실행할 수 없습니다.\n마감된 데이터를 선택에서 제외해주세요.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      return;
    }

    // 청크가 여러 번 나뉘어 비동기로 처리되므로, 실제로 뭔가 바뀌었을 때만
    // 되돌리기 스텝을 남기도록 시작 시점 상태를 남겨두고 끝난 뒤에 비교한다.
    const beforeUndoSnapshot = {
      modifiedDataMap: cloneModifiedDataMap(modifiedDataMap),
      aiAccountCodeMap: new Map(aiAccountCodeMap)
    };
    const beforeSerialized = serializeModifiedDataMap(modifiedDataMap);

    const checkedIds = Array.from(checkedDataSet);
    const idChunks = chunkArray(checkedIds, AI_SUGGESTION_CHUNK_SIZE);
    const tmpMCB = new Map(modifiedDataMap);
    const tmpAiAccountCodeMap = new Map(aiAccountCodeMap);

    aiSuggestionCancelRef.current = false;
    setIsAiSuggestionLoading(true);
    setAiSuggestionProgress({ total: checkedIds.length, completed: 0 });
    setIsAiSuggestionProgressOpen(true);

    const handleSuggestions = (
      suggestions: {
        id: string;
        accountCode?: string;
        isVatDeduction?: boolean | null;
        reasoning?: string;
        multiAccountCodes?: MultiAccountCodeAmount[] | null;
      }[]
    ) => {
      const reasoningMap = buildAiReasoningMap(suggestions);

      suggestions.forEach((suggestion) => {
        const originalData = dataList.find(
          (item) => item.id === suggestion.id
        ) as any;
        if (!originalData) return;

        // multiAccountCodes가 있으면 기존 "다중계정 설정" 기능과 동일하게 적용한다.
        // (금액 내림차순 정렬 후 최대 금액 계정코드를 대표 accountCode로 사용)
        if (suggestion.multiAccountCodes && suggestion.multiAccountCodes.length) {
          const sortedMulti = [...suggestion.multiAccountCodes].sort(
            (a, b) => b.amount - a.amount
          );
          // 다중계정 행은 단일 계정코드 재적용 캐시에서 제외한다.
          tmpAiAccountCodeMap.delete(suggestion.id);

          // 추천 결과가 원본(저장된 다중계정)과 같으면 변경분으로 남기지 않는다.
          const originalMulti: MultiAccountCodeAmount[] = Array.isArray(
            originalData.multiAccountCodes
          )
            ? [...originalData.multiAccountCodes].sort((a, b) => b.amount - a.amount)
            : [];
          const sameAsOriginal =
            originalMulti.length === sortedMulti.length &&
            originalMulti.every(
              (acc, i) =>
                acc.accountCode === sortedMulti[i].accountCode &&
                acc.amount === sortedMulti[i].amount
            );
          if (sameAsOriginal) {
            tmpMCB.delete(suggestion.id);
            return;
          }

          const existing = tmpMCB.get(suggestion.id);
          tmpMCB.set(suggestion.id, {
            ...(existing || {}),
            id: suggestion.id,
            accountCode: sortedMulti[0].accountCode,
            multiAccountCodes: sortedMulti
          });
          return;
        }

        // 단일 계정 추천: 이전에 AI가 넣어둔 다중계정 분할이 있으면 먼저 정리한다.
        const prevEntry = tmpMCB.get(suggestion.id);
        if (prevEntry?.multiAccountCodes?.length) {
          const cleaned = { ...prevEntry };
          delete cleaned.multiAccountCodes;
          tmpMCB.set(suggestion.id, cleaned);
        }

        mergeAiSuggestionIntoModifiedMap(
          tmpMCB,
          suggestion.id,
          suggestion.accountCode,
          originalData.accountCode,
          suggestion.isVatDeduction,
          originalData.isVatDeduction
        );

        if (suggestion.accountCode !== undefined) {
          if (suggestion.accountCode !== originalData.accountCode)
            tmpAiAccountCodeMap.set(suggestion.id, suggestion.accountCode);
          // 재추천 결과가 원본과 같아지면 이전 추천값 캐시(재적용 대상)도 지운다.
          else tmpAiAccountCodeMap.delete(suggestion.id);
        }
      });

      setModifiedDataMap(new Map(tmpMCB));
      setAiAccountCodeMap(new Map(tmpAiAccountCodeMap));

      if (reasoningMap.size > 0) {
        const attachReasoning = (
          list: (CardBuy | BankStatementWithAdditionalData)[]
        ) =>
          list.map((item) =>
            reasoningMap.has(item.id)
              ? { ...item, aiReasoning: reasoningMap.get(item.id) }
              : item
          );

        setDataList((prev) => attachReasoning(prev));
        setSortedDataList((prev) => attachReasoning(prev));
        setFilteredDataList((prev) => attachReasoning(prev));
      }
    };

    const requestChunk = (ids: string[]): Promise<void> => {
      if (storeType === "card") {
        return getCardBuyAiSuggestion(locationBizId, ids).then((res) => {
          const suggestions: AiSuggestionResult[] = res.data || [];
          handleSuggestions(suggestions);
        });
      } else if (storeType === "bank") {
        return getBankStatementAiSuggestion(locationBizId, ids).then(
          (res) => {
            const suggestions: BankAiSuggestionResult[] = res.data || [];
            handleSuggestions(suggestions);
          }
        );
      } else {
        const taxInvoiceIds: string[] = [];
        const taxfreeInvoiceIds: string[] = [];
        ids.forEach((id) => {
          const row = dataList.find((item) => item.id === id) as
            | (TaxInvoice & { id: string })
            | undefined;
          if (!row) return;
          if (row.type?.startsWith("TAXFREE_INVOICE"))
            taxfreeInvoiceIds.push(id);
          else taxInvoiceIds.push(id);
        });

        if (subType === "SALES") {
          return getTaxInvoiceSalesAiSuggestion(locationBizId, {
            taxInvoiceSalesIds: taxInvoiceIds,
            taxfreeInvoiceSalesIds: taxfreeInvoiceIds
          }).then((res) => {
            const suggestions: TaxInvoiceSalesAiSuggestionResult[] =
              res.data || [];
            handleSuggestions(suggestions);
          });
        } else {
          return getTaxInvoiceBuyAiSuggestion(locationBizId, {
            taxInvoiceBuyIds: taxInvoiceIds,
            taxfreeInvoiceBuyIds: taxfreeInvoiceIds
          }).then((res) => {
            const suggestions: TaxInvoiceBuyAiSuggestionResult[] =
              res.data || [];
            handleSuggestions(suggestions);
          });
        }
      }
    };

    const finishAiSuggestion = () => {
      setIsAiSuggestionProgressOpen(false);
      setIsAiSuggestionLoading(false);
      if (serializeModifiedDataMap(tmpMCB) !== beforeSerialized) {
        undoStackRef.current.push(beforeUndoSnapshot);
        redoStackRef.current = [];
      }
    };

    const runChunksSequentially = async () => {
      for (let i = 0; i < idChunks.length; i++) {
        if (aiSuggestionCancelRef.current) {
          finishAiSuggestion();
          return;
        }

        try {
          await requestChunk(idChunks[i]);
        } catch (err: any) {
          finishAiSuggestion();
          const tmpAlertData = { ...alertDialogData };
          tmpAlertData.type = "error";
          tmpAlertData.message =
            err.response?.data?.message ||
            "AI 회계처리 중 오류가 발생했습니다.";
          setAlertDialogData(tmpAlertData);
          setIsAlertOpen(true);
          return;
        }

        if (aiSuggestionCancelRef.current) {
          finishAiSuggestion();
          return;
        }

        setAiSuggestionProgress((prev) => ({
          ...prev,
          completed: prev.completed + idChunks[i].length
        }));
      }

      finishAiSuggestion();
      const tmpAlertData = { ...alertDialogData };
      tmpAlertData.type = "success";
      tmpAlertData.message = "AI 회계처리가 완료되었습니다.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
    };

    runChunksSequentially();
  };

  const requestCancelAiSuggestion = () => {
    setIsAiSuggestionCancelConfirmOpen(true);
  };

  const confirmCancelAiSuggestion = () => {
    aiSuggestionCancelRef.current = true;
    setIsAiSuggestionCancelConfirmOpen(false);
  };

  const closeCancelAiSuggestionConfirm = () => {
    setIsAiSuggestionCancelConfirmOpen(false);
  };

  const saveModified = (addAction?: string) => {
    const updateDataList: UpdateTxData[] = [];
    modifiedDataMap.forEach((data) => updateDataList.push(data));

    const paramData: { registerAccountCode?: boolean } = {};
    if (isRegisterAccountCode) paramData.registerAccountCode = true;

    const isConfirm = addAction === "confirm";
    if (isConfirm) setIsConfirmLoading(true);
    const saveRequest =
      storeType === "extra" && subType
        ? updateExtraInvoiceTransaction(subType, locationBizId, updateDataList)
        : updateTransaction(
            storeType,
            locationBizId,
            updateDataList,
            paramData
          );
    saveRequest
      .then((res) => {
        loadStore();
        if (isConfirm) handleConfirm();
      })
      .catch((err) => {
        if (isConfirm) setIsConfirmLoading(false);

        const errMsg = err.response?.data?.message || "오류가 발생했습니다.";
        if (errMsg) {
          const tmpAlertData = { ...alertDialogData };
          tmpAlertData.type = "error";
          tmpAlertData.message = errMsg;
          setAlertDialogData(tmpAlertData);
          setIsAlertOpen(true);
        }
      });
  };

  const handleConfirm = () => {
    const tmpAlertData = { ...alertDialogData };
    if (!business) {
      tmpAlertData.type = "error";
      tmpAlertData.message = "업체 정보를 불러오지 못했습니다.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      if (isConfirmLoading) setIsConfirmLoading(false);
      return;
    }

    if (!(spStartDate && spEndDate)) {
      tmpAlertData.type = "error";
      tmpAlertData.message = "검색 기간이 없습니다. 검색 기간을 설정해주세요.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      if (isConfirmLoading) setIsConfirmLoading(false);
      return;
    }

    const isConfirmed = business.isAccountingConfirmed;
    const paramData: BaseParamData = {
      startDate: spStartDate!,
      endDate: spEndDate!
    };
    if (subType) {
      const subTypeKey = SUB_TYPE_PARAM_KEYS[storeType] as SubType;
      if (subTypeKey) paramData[subTypeKey as keyof BaseParamData] = subType;
    }

    setIsConfirmLoading(true);
    const confirmRequest =
      storeType === "extra" && subType
        ? updateExtraInvoiceConfirmation(subType, business.id, !isConfirmed, {
            startDate: spStartDate!,
            endDate: spEndDate!
          })
        : updateConfirmation(storeType, business.id, !isConfirmed, paramData); // 마감 완료 버튼 입력시 isAccountingConfirmed = true
    confirmRequest
      .then(() => {
        setIsConfirmLoading(false);
        tmpAlertData.type = "success";
        tmpAlertData.message =
          (isConfirmed ? "마감 해제" : "마감 설정") + "되었습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
        loadStore();
      })
      .catch((err) => {
        setIsConfirmLoading(false);
        tmpAlertData.type = "error";
        tmpAlertData.message =
          (isConfirmed ? "마감 해제" : "마감 설정") +
          " 중 오류가 발생했습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
        console.error(err);
      });
  };

  const closeDeleteConfirmDialog = () => {
    setIsDeleteConfirmOpen(false);
  };

  const deleteSelected = () => {
    if (!subType || !locationBizId) {
      closeDeleteConfirmDialog();
      return;
    }

    const deleteIds = Array.from(checkedDataSet);
    if (deleteIds.length === 0) {
      closeDeleteConfirmDialog();
      return;
    }

    setIsDeleteLoading(true);
    deleteExtraInvoices(subType, locationBizId, deleteIds)
      .then(() => {
        setIsDeleteLoading(false);
        closeDeleteConfirmDialog();
        setCheckedDataSet(new Set());
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "success";
        tmpAlertData.message = "선택한 데이터가 삭제되었습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
        loadStore();
      })
      .catch((err) => {
        setIsDeleteLoading(false);
        closeDeleteConfirmDialog();
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message =
          err.response?.data?.message || "삭제 중 오류가 발생했습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  const checkModification = () => {
    if (modifiedDataMap.size > 0) {
      openUnsavedAlertDialog();
    } else {
      toDashboard();
    }
  };

  const toDashboard = () => {
    // setModifiedDataMap(new Map());
    // setCheckedDataSet(new Set());
    // setBusiness(null);
    const destination =
      spStartDate && spEndDate
        ? `/${menuType}?startDate=${spStartDate}&endDate=${spEndDate}`
        : `/${menuType}`;
    navigate(destination);
  };

  const openUnsavedAlertDialog = () => {
    setIsUnsavedAlertOpen(true);
  };

  const closeUnsavedAlertDialog = () => {
    setIsUnsavedAlertOpen(false);
  };

  const unprocessShowHandler = () => {
    setIsShowUnprocessed(!isShowUnprocessed);
  };

  const selectedOnlyShowHandler = () => {
    setIsShowSelectedOnly(!isShowSelectedOnly);
  };

  const notSentShowHandler = () => {
    setIsShowNotSent(!isShowNotSent);
  };

  const baseTableData = useMemo(
    () => (isFiltered ? filteredDataList : sortedDataList),
    [isFiltered, filteredDataList, sortedDataList]
  );

  const notSentCount = useMemo(
    () => dataList.filter((data) => !(data as any).newzenSent).length,
    [dataList]
  );

  useEffect(() => {
    if (!isShowUnprocessed) return;
    const unproData = baseTableData.filter(
      (data) =>
        !data.accountCode ||
        data.accountCode === "000" || // 임시출금
        data.accountCode === "001" // 임시입금
    );
    // 보여줄 미처리 데이터가 없는 경우 자동 해제
    if (unproData.length === 0) setIsShowUnprocessed(false);
  }, [isShowUnprocessed, baseTableData]);

  useEffect(() => {
    // 선택된 데이터가 없는 경우(저장, 필터 변경 등) 자동 해제
    if (isShowSelectedOnly && checkedDataSet.size === 0)
      setIsShowSelectedOnly(false);
  }, [isShowSelectedOnly, checkedDataSet]);

  useEffect(() => {
    // 보여줄 미전송 데이터가 없는 경우 자동 해제
    if (isShowNotSent && notSentCount === 0) setIsShowNotSent(false);
  }, [isShowNotSent, notSentCount]);

  const tableDataList = useMemo(() => {
    let list = baseTableData;

    if (isShowUnprocessed) {
      const unproData = list.filter(
        (data) =>
          !data.accountCode ||
          data.accountCode === "000" || // 임시출금
          data.accountCode === "001" // 임시입금
      );
      if (unproData.length > 0) list = unproData;
    }

    if (isShowSelectedOnly)
      list = list.filter((data) => checkedDataSet.has(data.id));

    if (isShowNotSent) {
      const notSentData = list.filter((data) => !(data as any).newzenSent);
      if (notSentData.length > 0) list = notSentData;
    }

    return list;
  }, [
    isShowUnprocessed,
    isShowSelectedOnly,
    isShowNotSent,
    baseTableData,
    checkedDataSet
  ]);

  const selectedSums = useMemo(() => {
    if (storeType !== "card" && storeType !== "tax" && storeType !== "extra")
      return null;

    let supplyAmount = 0;
    let vatAmount = 0;
    let totalAmount = 0;

    dataList.forEach((data) => {
      if (!checkedDataSet.has(data.id)) return;

      if (storeType === "card") {
        const cardData = data as CardBuy;
        const isDdc = modifiedDataMap.get(data.id)?.isVatDeduction;

        if (isDdc !== undefined) {
          supplyAmount += isDdc
            ? cardData.splCft
            : cardData.splCft + cardData.vaTxamt;
          vatAmount += isDdc ? cardData.vaTxamt : 0;
        } else {
          supplyAmount +=
            cardData.accSplCft || cardData.accSplCft === 0
              ? cardData.accSplCft
              : cardData.splCft;
          vatAmount +=
            cardData.accVaTxamt || cardData.accVaTxamt === 0
              ? cardData.accVaTxamt
              : cardData.vaTxamt;
        }
        totalAmount += cardData.totaTrsAmt;
      } else {
        const taxData = data as unknown as TaxInvoice;
        supplyAmount += taxData.supAmt;
        vatAmount += taxData.taxAmt;
        totalAmount += taxData.totAmt;
      }
    });

    return { supplyAmount, vatAmount, totalAmount };
  }, [checkedDataSet, dataList, modifiedDataMap, storeType]);

  const selectedBankSums = useMemo(() => {
    if (storeType !== "bank") return null;

    let inAmount = 0;
    let outAmount = 0;

    dataList.forEach((data) => {
      if (!checkedDataSet.has(data.id)) return;
      const bankData = data as BankStatementWithAdditionalData;
      inAmount += bankData.inAmt || 0;
      outAmount += bankData.outAmt || 0;
    });

    return { inAmount, outAmount };
  }, [checkedDataSet, dataList, storeType]);

  // Table에 넘기는 disableStatus/rowTooltip은 행 단위 memo가 깨지지 않도록
  // 선택 상태(checkedDataSet)와 무관하게 안정된 레퍼런스를 유지해야 한다.
  const tableDisableStatus = useMemo(
    () => ({
      input: (data: any) =>
        Boolean(data?.isAccountingConfirmed) ||
        Boolean(business?.isAccountingConfirmed),
      toggle: (data: any) =>
        Boolean(data?.isAccountingConfirmed) ||
        Boolean(business?.isAccountingConfirmed),
      openDialog: modifiedDataMap.size > 0,
      cancel: modifiedDataMap.size > 0
    }),
    [business?.isAccountingConfirmed, modifiedDataMap.size]
  );

  const tableRowTooltip = useCallback(
    (data: any) =>
      Boolean(data?.isAccountingConfirmed) ||
      Boolean(business?.isAccountingConfirmed)
        ? "마감된 데이터는 수정이 불가능합니다."
        : undefined,
    [business?.isAccountingConfirmed]
  );

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
      `}
    >
      <section
        css={css`
          padding-top: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        <div>
          <BaseButton
            onClick={checkModification}
            css={css`
              color: ${PALETTE.textSub};
              padding: 4px;

              &:hover {
                color: ${PALETTE.text};
              }
            `}
          >
            {"< 전체 업체 목록"}
          </BaseButton>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <h1>회계 검수</h1>
          {business && (
            <SubtitleCard>
              <div>{business.name}</div>
              <div>{business.regNumber}</div>
              {business.cluster && (
                <div>
                  {business.cluster}{" "}
                  {business.subCluster && <span>/ {business.subCluster}</span>}
                </div>
              )}
            </SubtitleCard>
          )}
        </div>
      </section>

      <section>
        <div
          css={css`
            margin-bottom: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
            `}
          >
            <BaseSelect
              name="반기/분기"
              options={reverseArray(HALF_AND_QUARTERS)}
              selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
              onSelect={onSelectQuarter}
            />
            <BaseSelect
              name="월별검색"
              options={reverseArray(MONTHS)}
              selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
              onSelect={onSelectMonth}
            />
            <BaseInput
              placeholder="검색어 입력"
              ref={inputKeywordRef}
              onInput={inputKeywordHandler}
              css={css`
                box-sizing: border-box;
              `}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            {isAiSuggestionAllowed &&
              (storeType === "card" ||
                storeType === "bank" ||
                storeType === "tax") && (
                <AiSuggestionButton
                  storeType={storeType}
                  subType={subType}
                  bizId={locationBizId}
                  startDisabled={
                    checkedDataSet.size === 0 ||
                    isLoading ||
                    isConfirmLoading ||
                    isAiSuggestionLoading ||
                    Boolean(business?.isAccountingConfirmed)
                  }
                  startDisabledReason={
                    business?.isAccountingConfirmed
                      ? "마감된 데이터는 수정이 불가능합니다."
                      : checkedDataSet.size === 0
                        ? "AI 회계처리를 적용할 데이터를 선택해주세요."
                        : ""
                  }
                  isStartLoading={isAiSuggestionLoading}
                  onStart={applyAiSuggestion}
                />
              )}
            {showInvoiceSumCheck && (
              <Tooltip
                title={
                  sumCheckDate
                    ? ""
                    : "합계표 검증은 월별 또는 분기별 검색 기간에서만 가능합니다."
                }
              >
                <div>
                  <BaseButton
                    variant="outlined"
                    color={PALETTE.primary}
                    disabled={!locationBizId || !sumCheckDate}
                    onClick={() => setIsInvoiceSumCheckOpen(true)}
                  >
                    합계표 검증
                  </BaseButton>
                </div>
              </Tooltip>
            )}
            {!isExtraMenu && (
              <Tooltip title="계정과목 입력이 완료된 데이터만 엑셀로 다운로드가 가능합니다.">
                <div>
                  <BaseButton
                    variant="outlined"
                    color={PALETTE.primary}
                    disabled={dataList.length === 0 || isDownloadLoading}
                    onClick={() => downloadExcel(storeType)}
                  >
                    다운로드
                  </BaseButton>
                </div>
              </Tooltip>
            )}
            {isExtraMenu && (
              <Tooltip
                title={
                  business?.isAccountingConfirmed
                    ? "마감된 데이터는 삭제할 수 없습니다."
                    : checkedDataSet.size === 0
                      ? "삭제할 데이터를 선택해주세요."
                      : ""
                }
              >
                <div>
                  <BaseButton
                    variant="outlined"
                    color={PALETTE.error}
                    disabled={
                      isLoading ||
                      isConfirmLoading ||
                      isDeleteLoading ||
                      checkedDataSet.size === 0 ||
                      modifiedDataMap.size > 0 ||
                      business?.isAccountingConfirmed
                    }
                    onClick={() => setIsDeleteConfirmOpen(true)}
                  >
                    삭제
                  </BaseButton>
                </div>
              </Tooltip>
            )}
            <Tooltip
              title={
                business?.isAccountingConfirmed
                  ? "마감된 데이터는 저장할 수 없습니다."
                  : modifiedDataMap.size === 0
                    ? "변경된 데이터가 없습니다."
                    : ""
              }
            >
              <Grid
                position="relative"
                onMouseEnter={() =>
                  storeType === "bank" && setIsSaveOptionOpen(true)
                }
                onMouseLeave={() => setIsSaveOptionOpen(false)}
              >
                <BaseButton
                  variant="contained"
                  disabled={
                    isLoading ||
                    isConfirmLoading ||
                    modifiedDataMap.size === 0 ||
                    business?.isAccountingConfirmed
                  }
                  color={PALETTE.primary}
                  onClick={() => saveModified()}
                >
                  저장
                  {storeType === "bank" && (
                    <>
                      {isRegisterAccountCode ? (
                        <CheckBoxIcon
                          sx={{
                            marginLeft: "2px",
                            marginRight: "-6px",
                            fontSize: "12px"
                          }}
                        />
                      ) : (
                        <CheckBoxOutlineBlankIcon
                          sx={{
                            marginLeft: "2px",
                            marginRight: "-6px",
                            fontSize: "12px"
                          }}
                        />
                      )}
                    </>
                  )}
                </BaseButton>
                {!(
                  isLoading ||
                  isConfirmLoading ||
                  modifiedDataMap.size === 0 ||
                  business?.isAccountingConfirmed
                ) &&
                  isSaveOptionOpen && (
                    <Box
                      minWidth="156px"
                      position="absolute"
                      top="-38px"
                      left="0px"
                      paddingRight="4px"
                      borderRadius="4px 4px 4px 0"
                      boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
                      bgcolor={PALETTE.light}
                    >
                      <FormControlLabel
                        label={
                          <Typography fontSize="13px" fontWeight={500}>
                            거래처-계정과목 매칭
                          </Typography>
                        }
                        sx={{ margin: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={isRegisterAccountCode}
                            onChange={() =>
                              setIsRegisterAccountCode(!isRegisterAccountCode)
                            }
                            css={css`
                              svg {
                                fill: ${PALETTE.primary};
                              }
                            `}
                          />
                        }
                      />
                    </Box>
                  )}
              </Grid>
            </Tooltip>
            <BaseButton
              variant={
                business && business.isAccountingConfirmed
                  ? "outlined"
                  : "contained"
              }
              color={PALETTE.primary}
              disabled={
                isLoading ||
                isConfirmLoading ||
                !(
                  business &&
                  business.totalDataNumber &&
                  business.completedDataNumber === business.totalDataNumber
                )
              }
              onClick={() => handleConfirm()}
            >
              {business?.isAccountingConfirmed ? "마감 해제" : "마감 설정"}
            </BaseButton>
          </div>
        </div>

        <div
          css={css`
            margin-bottom: 12px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            font-size: 16px;
            font-weight: 500;
          `}
        >
          {business ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 24px;
                font-size: 14px;
              `}
            >
              <div
                css={css`
                  font-size: 16px;
                  font-weight: 700;
                `}
              >
                총 {business.totalDataNumber}건
              </div>
              <UnprocessStatus
                isShow={isShowUnprocessed}
                showHandler={unprocessShowHandler}
                dataNum={
                  business.totalDataNumber - business.completedDataNumber
                }
              />
              <UnprocessStatus
                isShow={isShowNotSent}
                showHandler={notSentShowHandler}
                dataNum={notSentCount}
                label="미전송"
              />
              {(selectedSums || selectedBankSums) && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 16px;
                  `}
                >
                  <div>
                    선택{" "}
                    <BaseButton
                      variant={isShowSelectedOnly ? "contained" : "outlined"}
                      color={PALETTE.primary}
                      disabled={checkedDataSet.size === 0}
                      style={{
                        padding: "2px 6px 1px",
                        fontSize: "13px",
                        transform: "translateY(-1px)"
                      }}
                      onClick={selectedOnlyShowHandler}
                    >
                      {checkedDataSet.size}건
                    </BaseButton>
                  </div>
                  {selectedSums ? (
                    <>
                      <div>
                        공급가액{" "}
                        <strong>
                          {convertCurrency(selectedSums.supplyAmount)}
                        </strong>
                        원
                      </div>
                      <div>
                        부가세액{" "}
                        <strong>
                          {convertCurrency(selectedSums.vatAmount)}
                        </strong>
                        원
                      </div>
                      <div>
                        합계액{" "}
                        <strong>
                          {convertCurrency(selectedSums.totalAmount)}
                        </strong>
                        원
                      </div>
                    </>
                  ) : (
                    selectedBankSums && (
                      <>
                        <div>
                          입금{" "}
                          <strong>
                            {convertCurrency(selectedBankSums.inAmount)}
                          </strong>
                          원
                        </div>
                        <div>
                          출금{" "}
                          <strong>
                            {convertCurrency(selectedBankSums.outAmount)}
                          </strong>
                          원
                        </div>
                      </>
                    )
                  )}
                </div>
              )}
            </div>
          ) : (
            <div></div>
          )}

          <div
            css={css`
              position: relative;
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            {(menuType === "card" || menuType === "tax-buy") && (
              <BaseSelect
                name="공제여부"
                color={PALETTE.primary}
                minWidth={120}
                options={[
                  { value: "y", label: "공제" },
                  { value: "n", label: "불공제" }
                ]}
                selectedOption={selectedDdcAll}
                onSelect={onSelectDdcAll}
                css={css`
                  padding: 6px 12px;
                `}
              />
            )}
            <Box
              css={css`
                position: relative;
              `}
            >
              <BaseInput
                placeholder="계정코드 입력"
                color={PALETTE.primary}
                ref={inputSubjectRef}
                onInput={onSearchSbjAll}
                onKeyDown={keyDownHanlder}
                onFocus={() => {
                  setIsSubjectListOpen(true);
                }}
                onBlur={(e) => {
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (
                    !relatedTarget ||
                    relatedTarget?.tagName !== "LI" ||
                    relatedTarget?.parentNode !== optionSubjectRef.current
                  ) {
                    setIsSubjectListOpen(false);
                  }
                }}
                css={css`
                  box-sizing: border-box;
                  padding: 8px 12px;
                `}
              />
              {isSubjectListOpen && (
                <ul
                  css={css`
                    margin: 0;
                    padding: 0;
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 100%;
                    max-height: 200px;
                    overflow-y: auto;
                    border-radius: 4px;
                    box-sizing: border-box;
                    border: 1px solid ${PALETTE.border};
                    background-color: white;
                    z-index: 20;
                    list-style: none;
                  `}
                  ref={optionSubjectRef}
                >
                  {filteredSubjectList.map((subject) => (
                    <li
                      key={subject.value}
                      tabIndex={-1}
                      onClick={() => {
                        onSelectSbjAll({
                          currentTarget: { value: subject.value }
                        } as unknown as React.MouseEvent<HTMLOptionElement>);
                        setIsSubjectListOpen(false);
                      }}
                      css={css`
                        padding: 8px 12px;
                        cursor: pointer;
                        list-style: none;
                        font-size: 14px;

                        &:hover {
                          background-color: ${PALETTE.primaryHover};
                        }
                      `}
                    >
                      {subject.label}
                    </li>
                  ))}
                </ul>
              )}
            </Box>

            {menuType === "bank" && (
              <Grid>
                <BaseButton
                  variant="outlined"
                  color={PALETTE.primary}
                  onClick={onSelectStoreAll}
                  css={css`
                    padding: 8px 12px;
                    font-size: 14px;
                  `}
                >
                  거래처 변경
                </BaseButton>
              </Grid>
            )}

            <Tooltip
              title={
                isLoading || isConfirmLoading
                  ? "로딩중입니다."
                  : modifiedDataMap.size > 0
                    ? "수정 중인 데이터가 있습니다. 저장 후 전송 작업을 진행해주세요."
                    : business
                      ? ""
                      : "업체 정보를 불러오지 못했습니다."
              }
            >
              <div>
                <ReportButton
                  storeType={storeType}
                  subType={subType}
                  bizId={locationBizId}
                  selectedDataIds={Array.from(checkedDataSet)}
                  disabled={
                    checkedDataSet.size === 0 ||
                    isLoading ||
                    isConfirmLoading ||
                    modifiedDataMap.size > 0
                  }
                  variant="contained"
                  successCallback={loadStore}
                >
                  세무사랑
                </ReportButton>
              </div>
            </Tooltip>
          </div>
        </div>

        <Table
          isLoading={isLoading}
          minWidth={1240}
          sortLimit={2}
          selectable={true}
          disableStatus={tableDisableStatus}
          rowTooltip={tableRowTooltip}
          onPaste={tablePaste}
          checkedSet={checkedDataSet}
          setCheckedSet={setCheckedDataSet}
          modifiedMap={modifiedDataMap}
          columns={columnList}
          dataList={tableDataList}
          subjectList={subjectCodeDataList}
        />
      </section>

      <Dialog open={isUnsavedAlertOpen} onClose={closeUnsavedAlertDialog}>
        <DialogTitle>알림</DialogTitle>
        <DialogContent>
          <DialogContentText>
            저장되지 않은 데이터가 있습니다.
          </DialogContentText>
          <DialogContentText>
            변경사항을 저장하지 않고 이동하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          css={css`
            margin: 0 12px;
            padding: 12px;
            border-top: 1px solid ${PALETTE.border};
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={closeUnsavedAlertDialog}
            css={css`
              padding: 8px 16px;
            `}
          >
            취소
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            onClick={toDashboard}
            css={css`
              padding: 8px 16px;
            `}
          >
            확인
          </BaseButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={closeDeleteConfirmDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>알림</DialogTitle>
        <DialogContent>
          <DialogContentText>
            선택한 {checkedDataSet.size}건을 삭제하시겠습니까?
          </DialogContentText>
          <DialogContentText>
            삭제한 데이터는 복구할 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          css={css`
            margin: 0 12px;
            padding: 12px;
            border-top: 1px solid ${PALETTE.border};
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={closeDeleteConfirmDialog}
            css={css`
              padding: 8px 16px;
            `}
          >
            취소
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.error}
            disabled={isDeleteLoading}
            onClick={deleteSelected}
            css={css`
              padding: 8px 16px;
            `}
          >
            {isDeleteLoading ? "삭제 중" : "삭제"}
          </BaseButton>
        </DialogActions>
      </Dialog>
      <Dialog open={isAiSuggestionProgressOpen} maxWidth="xs" fullWidth>
        <DialogTitle>AI 회계처리 진행중</DialogTitle>
        <DialogContent>
          <LinearProgress
            variant="determinate"
            value={
              aiSuggestionProgress.total > 0
                ? (aiSuggestionProgress.completed /
                    aiSuggestionProgress.total) *
                  100
                : 0
            }
          />
          <Typography
            css={css`
              margin-top: 4px;
              text-align: right;
              font-size: 13px;
              color: ${PALETTE.textMid};
            `}
          >
            {aiSuggestionProgress.completed}/{aiSuggestionProgress.total}
          </Typography>
        </DialogContent>
        <DialogActions
          css={css`
            margin: 0 12px;
            padding: 12px;
            border-top: 1px solid ${PALETTE.border};
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={requestCancelAiSuggestion}
            css={css`
              padding: 8px 16px;
            `}
          >
            취소
          </BaseButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isAiSuggestionCancelConfirmOpen}
        onClose={closeCancelAiSuggestionConfirm}
      >
        <DialogTitle>알림</DialogTitle>
        <DialogContent>
          <DialogContentText>
            회계처리가 진행중입니다. 취소하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          css={css`
            margin: 0 12px;
            padding: 12px;
            border-top: 1px solid ${PALETTE.border};
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={closeCancelAiSuggestionConfirm}
            css={css`
              padding: 8px 16px;
            `}
          >
            아니오
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.error}
            onClick={confirmCancelAiSuggestion}
            css={css`
              padding: 8px 16px;
            `}
          >
            취소
          </BaseButton>
        </DialogActions>
      </Dialog>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
      {selectedMAData && (
        <MultiAccountDialog
          isOpen={isMultiAccountDialogOpen}
          onClose={closeMultiAccountCodeDialog}
          data={selectedMAData}
          subjectList={modifiedSubjectList}
          successCallback={loadStore}
        />
      )}
      <SelectStoreDialog
        isOpen={isSelectDialogOpen}
        onClose={closeSelectStoreDialog}
        settings={selectedStoreDialogSettings}
        storeList={storeList}
        businessId={locationBizId}
        successCallback={loadStore}
      />
      {showInvoiceSumCheck && subType && sumCheckDate && locationBizId && (
        <InvoiceSumCheckDialog
          isOpen={isInvoiceSumCheckOpen}
          onClose={() => setIsInvoiceSumCheckOpen(false)}
          businessId={locationBizId}
          invoiceType={subType}
          date={sumCheckDate}
        />
      )}
    </div>
  );
}

const UnprocessStatus = ({
  isShow,
  showHandler,
  dataNum,
  label = "미처리"
}: {
  isShow: boolean;
  showHandler: () => void;
  dataNum: number;
  label?: string;
}) => {
  return (
    <div>
      {label}{" "}
      {dataNum > 0 || isShow ? (
        <BaseButton
          variant={isShow ? "contained" : "outlined"}
          color={PALETTE.error}
          style={{
            padding: "2px 6px 1px",
            fontSize: "13px",
            transform: "translateY(-1px)"
          }}
          onClick={showHandler}
        >
          {dataNum}건
        </BaseButton>
      ) : (
        <span>{dataNum}건</span>
      )}
    </div>
  );
};

export default Review;
